import React, { useContext } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppDataProvider/AppDataProvider";
import { ActionType } from "../../AppDataProvider/actions";
import { Button } from "../../common/components";
import { requireBookingDataFields } from "../../utils";
import { BookingData } from "../../AppDataProvider/types";
import { PartRequired } from "../../utils/types";
import { marginBottom, marginTop, paddingX, paddingBottom, typography, corners } from "../../common/styles";
import { applyOpacity } from "../../common/styles/utils";
type WaitlistConfirmationBookingData = PartRequired<BookingData, "organisation" | "service" | "timezone">;
export function WaitlistConfirmation(): React.ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  requireBookingDataFields<WaitlistConfirmationBookingData>(appData.bookingData, ["organisation", "service", "timezone"]);
  const {
    organisation,
    service,
    timezone
  } = appData.bookingData;
  const {
    waitlistBooking
  } = appData;
  const eventDateDisplay = waitlistBooking ? new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: timezone
  }).format(waitlistBooking.startTime) : "";
  const eventTimeDisplay = waitlistBooking ? `${new Intl.DateTimeFormat(i18n.language, {
    timeStyle: "short",
    timeZone: timezone
  }).format(waitlistBooking.startTime)} - ${new Intl.DateTimeFormat(i18n.language, {
    timeStyle: "short",
    timeZone: timezone
  }).format(waitlistBooking.endTime)}` : "";
  const durationMs = waitlistBooking ? waitlistBooking.endTime.getTime() - waitlistBooking.startTime.getTime() : 0;
  const durationMinutes = Math.round(durationMs / 60000);
  const durationDisplay = durationMinutes >= 60 ? `${Math.floor(durationMinutes / 60)}h${durationMinutes % 60 ? ` ${durationMinutes % 60}min` : ""}` : `${durationMinutes}min`;
  const handleJoinAnother = () => {
    appDataDispatch({
      type: ActionType.BACK_FROM_WAITLIST_STEP
    });
  };
  return <Container data-sentry-element="Container" data-sentry-component="WaitlistConfirmation" data-sentry-source-file="WaitlistConfirmation.tsx">
      <StatusIcon data-sentry-element="StatusIcon" data-sentry-source-file="WaitlistConfirmation.tsx">&#9203;</StatusIcon>
      <Title data-sentry-element="Title" data-sentry-source-file="WaitlistConfirmation.tsx">{t("waitlist.onTheWaitlist", "On the waitlist")}</Title>

      <EventCard data-sentry-element="EventCard" data-sentry-source-file="WaitlistConfirmation.tsx">
        <EventLabel data-sentry-element="EventLabel" data-sentry-source-file="WaitlistConfirmation.tsx">{t("waitlist.event", "Event")}</EventLabel>
        <EventValue data-sentry-element="EventValue" data-sentry-source-file="WaitlistConfirmation.tsx">{waitlistBooking?.serviceName ?? service.name}</EventValue>

        <EventLabel data-sentry-element="EventLabel" data-sentry-source-file="WaitlistConfirmation.tsx">{organisation.name}</EventLabel>

        <EventLabel data-sentry-element="EventLabel" data-sentry-source-file="WaitlistConfirmation.tsx">{t("waitlist.requested", "Requested")}</EventLabel>
        <EventValue data-sentry-element="EventValue" data-sentry-source-file="WaitlistConfirmation.tsx">{eventDateDisplay}</EventValue>
        <EventValue data-sentry-element="EventValue" data-sentry-source-file="WaitlistConfirmation.tsx">{eventTimeDisplay}</EventValue>

        <EventLabel data-sentry-element="EventLabel" data-sentry-source-file="WaitlistConfirmation.tsx">{t("waitlist.duration", "Duration")}</EventLabel>
        <EventValue data-sentry-element="EventValue" data-sentry-source-file="WaitlistConfirmation.tsx">{durationDisplay}</EventValue>
      </EventCard>

      <Message data-sentry-element="Message" data-sentry-source-file="WaitlistConfirmation.tsx">
        {t("waitlist.confirmationMessage", "We'll email you the moment a spot opens up.")}
      </Message>

      <JoinAnotherButton onClick={handleJoinAnother} data-sentry-element="JoinAnotherButton" data-sentry-source-file="WaitlistConfirmation.tsx">
        {t("waitlist.joinAnother", "Join another waitlist")}
      </JoinAnotherButton>
    </Container>;
}
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const StatusIcon = styled.div`
  font-size: 2.5rem;
  ${marginBottom.m}
`;
const Title = styled.h2`
  ${typography.heading}
  ${marginBottom.l}
`;
const EventCard = styled.div`
  width: 100%;
  ${corners.m}
  ${paddingX.m}
  ${paddingBottom.m}
  padding-top: 1rem;
  ${marginBottom.l}
  background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.03)};
  border: 1px solid ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.1)};
  text-align: left;
`;
const EventLabel = styled.div`
  ${typography.small}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
  ${marginTop.s}
  &::first-letter {
    text-transform: capitalize;
  }
`;
const EventValue = styled.div`
  ${typography.body}
`;
const Message = styled.p`
  ${typography.body}
  ${marginBottom.l}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.7)};
`;
const JoinAnotherButton = styled(Button)`
  width: 100%;
  ${paddingX.l}
`;